import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Wand2, Plus, Copy, Check, ChevronDown, ChevronUp, Lock, Calendar, Download, Edit, Save, X } from 'lucide-react';
import { stringify } from 'csv-stringify/browser/esm/sync';
import { SmartSearchBar } from '../Layout/SmartSearchBar';
import { TextHighlighter } from './TextHighlighter';
import { ImageUpload } from './ImageUpload';
import { useAuth } from '../../hooks/useAuth';
import { useListingTemplates } from '../../hooks/useListingTemplates';
import { usePrompts } from '../../hooks/usePrompts';
import { openAIService } from '../../services/openai';
import { ListingTemplate, ListingTemplateVariation } from '../../types';

export const ViewListingTemplatePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { getTemplateById, addVariation, getTemplateVariations, updateTemplate } = useListingTemplates(user?.id || null);
  
  // We need the prompts hook for the sidebar filters, but we'll use empty filters
  const {
    filters,
    setFilters,
    getAllPlatforms,
    getAllDomains,
    getAllOutputs,
    getAllWebPlatforms,
  } = usePrompts(user?.id || null);

  const [template, setTemplate] = useState<ListingTemplate | null>(null);
  const [variations, setVariations] = useState<ListingTemplateVariation[]>([]);
  const [variationInstruction, setVariationInstruction] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const [expandedVariations, setExpandedVariations] = useState<Set<string>>(new Set());

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    masterTitle: '',
    masterDescription: '',
    imageUrl: null as string | null,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      loadTemplateData();
    }
  }, [id]);

  useEffect(() => {
    if (template) {
      setEditFormData({
        name: template.name,
        masterTitle: template.masterTitle,
        masterDescription: template.masterDescription,
        imageUrl: template.imageUrl || null,
      });
    }
  }, [template]);

  const loadTemplateData = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);

      const [templateData, variationsData] = await Promise.all([
        getTemplateById(id),
        getTemplateVariations(id),
      ]);

      setTemplate(templateData);
      setVariations(variationsData);
    } catch (err) {
      console.error('Error loading template data:', err);
      setError('Failed to load template data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = (imageUrl: string | null) => {
    setEditFormData(prev => ({ ...prev, imageUrl }));
  };

  const handleLockedElementsChange = async (newLockedElements: string[]) => {
    if (!template) return;

    try {
      await updateTemplate(template.id, { lockedElements: newLockedElements });
      setTemplate(prev => prev ? { ...prev, lockedElements: newLockedElements } : null);
    } catch (err) {
      console.error('Error updating locked elements:', err);
      setError('Failed to update locked elements');
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
    if (template) {
      setEditFormData({
        name: template.name,
        masterTitle: template.masterTitle,
        masterDescription: template.masterDescription,
        imageUrl: template.imageUrl || null,
      });
    }
  };

  const handleSaveEdit = async () => {
    if (!template || !editFormData.name.trim() || !editFormData.masterTitle.trim() || !editFormData.masterDescription.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await updateTemplate(template.id, {
        name: editFormData.name.trim(),
        masterTitle: editFormData.masterTitle.trim(),
        masterDescription: editFormData.masterDescription.trim(),
        imageUrl: editFormData.imageUrl || undefined,
      });

      setTemplate(prev => prev ? {
        ...prev,
        name: editFormData.name.trim(),
        masterTitle: editFormData.masterTitle.trim(),
        masterDescription: editFormData.masterDescription.trim(),
        imageUrl: editFormData.imageUrl || undefined,
      } : null);

      setIsEditing(false);
    } catch (err) {
      console.error('Error updating template:', err);
      setError(err instanceof Error ? err.message : 'Failed to update template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateVariation = async () => {
    if (!template || !variationInstruction.trim()) {
      setError('Please enter a variation instruction.');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const result = await openAIService.generateListingVariation({
        masterTitle: template.masterTitle,
        masterDescription: template.masterDescription,
        variationInstruction: variationInstruction.trim(),
        lockedElements: template.lockedElements,
        imageUrl: template.imageUrl,
      });

      const newVariation = await addVariation(template.id, {
        variationInstruction: variationInstruction.trim(),
        generatedTitle: result.title,
        generatedDescription: result.description,
      });

      setVariations(prev => [newVariation, ...prev]);
      setVariationInstruction('');
      
      // Auto-expand the new variation
      setExpandedVariations(prev => new Set([...prev, newVariation.id]));
    } catch (err) {
      console.error('Error generating variation:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate variation. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const toggleVariationExpansion = (variationId: string) => {
    setExpandedVariations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  const handleDownloadCSV = () => {
    if (!template || variations.length === 0) {
      alert('No variations to download');
      return;
    }

    const csvData = [
      {
        'Type': 'Master Template',
        'Instruction': 'Original',
        'Title': template.masterTitle,
        'Description': template.masterDescription,
        'Created': template.createdAt.toISOString(),
      },
      ...variations.map((variation, index) => ({
        'Type': 'Variation',
        'Instruction': variation.variationInstruction,
        'Title': variation.generatedTitle,
        'Description': variation.generatedDescription,
        'Created': variation.createdAt.toISOString(),
      }))
    ];

    const csvString = stringify(csvData, {
      header: true,
      columns: ['Type', 'Instruction', 'Title', 'Description', 'Created']
    });

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${template.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-variations-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSearchChange = () => {
    // No search functionality for listing templates yet
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (!user) return null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fire-50 via-orange-50 to-ember-50 
        dark:bg-dark-gradient transition-all duration-500">
        
        {/* Floating Search Bar */}
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-md lg:max-w-xl xl:max-w-2xl px-4">
          <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl border border-gray-200/60 dark:border-dark-border/40 rounded-lg shadow-sm">
            <div className="p-2">
              <SmartSearchBar
                searchQuery=""
                onSearchChange={handleSearchChange}
                prompts={[]}
                recentlyExecuted={[]}
              />
            </div>
          </div>
        </div>

        <main className="flex-1 p-6 pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-modular-stone-alloy 
                    border-t-modular-frosted-indigo mx-auto"></div>
                  <Wand2 className="absolute inset-0 h-6 w-6 text-fire-500 m-auto animate-pulse" />
                </div>
                <p className="text-gray-600 dark:text-dark-text-secondary font-medium">
                  Loading template details...
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fire-50 via-orange-50 to-ember-50 
        dark:bg-dark-gradient transition-all duration-500">
        
        {/* Floating Search Bar */}
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-md lg:max-w-xl xl:max-w-2xl px-4">
          <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl border border-gray-200/60 dark:border-dark-border/40 rounded-lg shadow-sm">
            <div className="p-2">
              <SmartSearchBar
                searchQuery=""
                onSearchChange={handleSearchChange}
                prompts={[]}
                recentlyExecuted={[]}
              />
            </div>
          </div>
        </div>

        <main className="flex-1 p-6 pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-16">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-3">
                Template not found
              </h3>
              <p className="text-gray-600 dark:text-dark-text-secondary mb-8">
                The template you're looking for doesn't exist or couldn't be loaded.
              </p>
              
              <button
                onClick={() => navigate('/listing-templates')}
                className="bg-modular-primary-gradient text-white px-6 py-3 rounded-lg hover:shadow-modular-glow 
                  transition-all duration-300 shadow-fire-card transform hover:scale-105 
                  active:scale-95 flex items-center mx-auto"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Templates
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-canvas-fog via-modular-ivory-mist to-modular-lunar-sage 
      dark:bg-dark-gradient transition-all duration-500">
      
      {/* Floating Search Bar */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-md lg:max-w-xl xl:max-w-2xl px-4">
        <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl border border-gray-200/60 dark:border-dark-border/40 rounded-lg shadow-sm">
          <div className="p-2">
            <SmartSearchBar
              searchQuery=""
              onSearchChange={handleSearchChange}
              prompts={[]}
              recentlyExecuted={[]}
            />
          </div>
        </div>
      </div>

      <main className="flex-1 p-6 pt-20">
        <div className="w-full">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/listing-templates')}
                className="mr-4 p-2 text-gray-600 dark:text-gray-300 hover:text-fire-600 
                  dark:hover:text-modular-frosted-indigo hover:bg-modular-canvas-fog dark:hover:bg-modular-shadow-quartz/20 
                  rounded-lg transition-all duration-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              
              <div>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditInputChange}
                    className="text-3xl font-bold bg-transparent border-b-2 border-fire-500 
                      text-gray-900 dark:text-dark-text focus:outline-none focus:border-fire-600 
                      transition-colors duration-300 min-w-[300px]"
                    disabled={isSaving}
                  />
                ) : (
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">
                    {template?.name}
                  </h1>
                )}
                <div className="flex items-center space-x-3 mt-1">
                  <span className="text-gray-600 dark:text-dark-text-secondary text-sm">
                    Created {template && formatDate(template.createdAt)}
                  </span>
                  <span className="text-fire-600 dark:text-fire-400 text-sm font-medium">
                    {variations.length} variation{variations.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 
                      dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 
                      transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                      flex items-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving || !editFormData.name.trim() || !editFormData.masterTitle.trim() || !editFormData.masterDescription.trim()}
                    className="bg-fire-gradient text-white px-6 py-2 rounded-lg hover:shadow-fire-glow 
                      transition-all duration-300 shadow-fire-card transform hover:scale-105 
                      active:scale-95 flex items-center font-semibold disabled:opacity-50 
                      disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleStartEdit}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg 
                      transition-all duration-300 shadow-sm transform hover:scale-105 
                      active:scale-95 flex items-center font-semibold"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Template
                  </button>
                  
                  {variations.length > 0 && (
                    <button
                      onClick={handleDownloadCSV}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg 
                        transition-all duration-300 shadow-sm transform hover:scale-105 
                        active:scale-95 flex items-center font-semibold"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download CSV
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Generate New Variation - Hidden during edit mode */}
          {!isEditing && (
            <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl border 
              border-modular-stone-alloy/30 dark:border-modular-graphite-smoke/20 p-6 mb-8 shadow-modular-card 
              dark:shadow-dark-card space-y-4">
              
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-4 flex items-center">
                <Plus className="h-5 w-5 mr-2 text-fire-500 dark:text-fire-400" />
                Generate New Variation
              </h2>

              {/* Show image related info when image exists */}
              {template?.imageUrl && (
                <div className="flex items-start space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex-shrink-0">
                    <img
                      src={template.imageUrl} 
                      alt="Design preview" 
                      className="w-24 h-24 object-cover rounded-lg border border-blue-300 dark:border-blue-700"
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                      Using Design Image
                    </h4>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      The AI will analyze this image to create more accurate and relevant variations
                      based on your product's visual appearance.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="variationInstruction" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Variation Instruction
                  </label>
                  <input
                    type="text"
                    id="variationInstruction"
                    value={variationInstruction}
                    onChange={(e) => setVariationInstruction(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg 
                      focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                      bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text 
                      placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                    placeholder="e.g., Tailor this listing to Egypt instead of NYC"
                    disabled={isGenerating}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Describe how you want the listing to be changed. Be specific about what should be different.
                  </p>
                </div>

                <button
                  onClick={handleGenerateVariation}
                  disabled={!variationInstruction.trim() || isGenerating}
                  className="bg-fire-gradient text-white px-6 py-3 rounded-lg hover:shadow-fire-glow 
                    focus:outline-none focus:ring-2 focus:ring-fire-500 focus:ring-offset-2 
                    transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed 
                    shadow-fire-card transform hover:scale-105 active:scale-95 
                    flex items-center font-semibold"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-5 w-5 mr-2" />
                      Generate Variation
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Master Template */}
          {template && (
            <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl border 
              border-modular-stone-alloy/30 dark:border-modular-graphite-smoke/20 p-6 mb-8 shadow-modular-card 
              dark:shadow-dark-card">
              
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text flex items-center">
                  <Wand2 className="h-5 w-5 mr-2 text-fire-500 dark:text-fire-400" />
                  Master Template
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Master Title */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Master Title:
                    </p>
                    {!isEditing && (
                      <button
                        onClick={() => handleCopy(template.masterTitle, 'master-title')}
                        className={`flex items-center px-3 py-1 text-xs rounded-lg transition-all duration-300 ${
                          copiedStates['master-title']
                            ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                            : 'text-gray-600 dark:text-gray-300 hover:text-fire-600 dark:hover:text-fire-400 hover:bg-fire-50 dark:hover:bg-fire-900/20'
                        }`}
                      >
                        {copiedStates['master-title'] ? (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 mr-1" />
                            Copy
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <input
                      type="text"
                      name="masterTitle"
                      value={editFormData.masterTitle}
                      onChange={handleEditInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg 
                        focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                        bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text 
                        placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                      placeholder="Enter master title..."
                      disabled={isSaving}
                    />
                  ) : (
                    <div className="bg-gray-50 dark:bg-dark-card rounded-lg p-3 border border-gray-200 dark:border-dark-border">
                      <p className="text-gray-900 dark:text-dark-text font-medium">
                        {template.masterTitle}
                      </p>
                    </div>
                  )}
                </div>

                {/* Master Description */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Master Description:
                    </p>
                    {!isEditing && (
                      <button
                        onClick={() => handleCopy(template.masterDescription, 'master-description')}
                        className={`flex items-center px-3 py-1 text-xs rounded-lg transition-all duration-300 ${
                          copiedStates['master-description']
                            ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                            : 'text-gray-600 dark:text-gray-300 hover:text-fire-600 dark:hover:text-fire-400 hover:bg-fire-50 dark:hover:bg-fire-900/20'
                        }`}
                      >
                        {copiedStates['master-description'] ? (
                          <>
                            <Check className="h-3 w-3 mr-1" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3 mr-1" />
                            Copy
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <textarea
                      name="masterDescription"
                      value={editFormData.masterDescription}
                      onChange={handleEditInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg 
                        focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                        bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text 
                        placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300 resize-y"
                      placeholder="Enter master description..."
                      disabled={isSaving}
                    />
                  ) : (
                    <div className="bg-gray-50 dark:bg-dark-card rounded-lg p-3 border border-gray-200 dark:border-dark-border">
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        {template.masterDescription}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Image Upload - Edit mode */}
              {isEditing && (
                <div className="mt-6 border-t border-gray-200 dark:border-dark-border pt-6">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Design Image:
                  </h3>
                  <ImageUpload
                    imageUrl={editFormData.imageUrl}
                    onImageChange={handleImageChange}
                    disabled={isSaving}
                  />
                </div>
              )}
              
              {/* Image Display - View mode */}
              {!isEditing && template.imageUrl && (
                <div className="mt-6 border-t border-gray-200 dark:border-dark-border pt-6">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                    Design Image:
                  </p>
                  <div className="bg-gray-50 dark:bg-dark-card rounded-lg p-4 border border-gray-200 dark:border-dark-border">
                    <img
                      src={template.imageUrl}
                      alt="Template design"
                      className="w-full max-w-md h-48 object-cover rounded-lg"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Interactive Locked Elements Management - Standalone Section */}
          {template && (
            <div className="mb-8">
              <TextHighlighter
                text={isEditing ? editFormData.masterDescription : template.masterDescription}
                lockedElements={template.lockedElements}
                onLockedElementsChange={handleLockedElementsChange}
                label="Manage Locked Elements"
                placeholder="No description available"
                disabled={isSaving}
              />
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Variations List - Hidden during edit mode */}
          {!isEditing && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-6">
                Generated Variations ({variations.length})
              </h2>

              {variations.length === 0 ? (
                <div className="text-center py-12 bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl 
                  border border-gray-200/30 dark:border-gray-600/20">
                  <Wand2 className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
                    No variations yet
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Generate your first variation using the form above.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {variations.map((variation, index) => (
                    <div
                      key={variation.id}
                      className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-sm rounded-xl 
                        border border-gray-200/30 dark:border-gray-600/20 overflow-hidden
                        hover:shadow-lg transition-all duration-300"
                    >
                      {/* Variation Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 
                          transition-colors duration-300"
                        onClick={() => toggleVariationExpansion(variation.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                Variation {variations.length - index}
                              </span>
                              <span className="ml-3 text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(variation.createdAt)}
                              </span>
                            </div>
                            <p className="text-fire-700 dark:text-fire-300 font-medium">
                              "{variation.variationInstruction}"
                            </p>
                          </div>
                          <div className="ml-4">
                            {expandedVariations.has(variation.id) ? (
                              <ChevronUp className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Variation Content */}
                      {expandedVariations.has(variation.id) && (
                        <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-800/30">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Generated Title */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                  Generated Title:
                                </p>
                                <button
                                  onClick={() => handleCopy(variation.generatedTitle, `title-${variation.id}`)}
                                  className={`flex items-center px-3 py-1 text-xs rounded-lg transition-all duration-300 ${
                                    copiedStates[`title-${variation.id}`]
                                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                      : 'text-gray-600 dark:text-gray-300 hover:text-fire-600 dark:hover:text-fire-400 hover:bg-fire-50 dark:hover:bg-fire-900/20'
                                  }`}
                                >
                                  {copiedStates[`title-${variation.id}`] ? (
                                    <>
                                      <Check className="h-3 w-3 mr-1" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-3 w-3 mr-1" />
                                      Copy
                                    </>
                                  )}
                                </button>
                              </div>
                              <div className="bg-white dark:bg-dark-card rounded-lg p-3 border border-gray-200 dark:border-dark-border">
                                <p className="text-gray-900 dark:text-dark-text font-medium">
                                  {variation.generatedTitle}
                                </p>
                              </div>
                            </div>

                            {/* Generated Description */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                  Generated Description:
                                </p>
                                <button
                                  onClick={() => handleCopy(variation.generatedDescription, `description-${variation.id}`)}
                                  className={`flex items-center px-3 py-1 text-xs rounded-lg transition-all duration-300 ${
                                    copiedStates[`description-${variation.id}`]
                                      ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                      : 'text-gray-600 dark:text-gray-300 hover:text-fire-600 dark:hover:text-fire-400 hover:bg-fire-50 dark:hover:bg-fire-900/20'
                                  }`}
                                >
                                  {copiedStates[`description-${variation.id}`] ? (
                                    <>
                                      <Check className="h-3 w-3 mr-1" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-3 w-3 mr-1" />
                                      Copy
                                    </>
                                  )}
                                </button>
                              </div>
                              <div className="bg-white dark:bg-dark-card rounded-lg p-3 border border-gray-200 dark:border-dark-border">
                                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                  {variation.generatedDescription}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};